import { useCallback, useMemo, useState } from "react";
import { IconClose, IconSearch } from "@synthesis/shared-icons";
import { observer } from "mobx-react-lite";
import { Button, Checkbox, Modal } from "@synthesis/shared-ui";

import { AdminModalBody, AdminModalFooter } from "../../../components/admin-console/admin-modal";
import { useRailways } from "../../../shared/hooks/use-railways";
import { MOCK_CONSTRUCTOR_ROADS } from "../../constructor-roads-page/constructor-roads-page.lib";
import type { CreateApplicationSelectOption } from "../../create-application-page/create-application-dictionaries.lib";
import { useServiceDetail } from "../hooks/use-service-detail";
import { MOCK_SERVICE_PROCESS_STEPS, MOCK_SERVICE_PROCESS_VERSIONS, usePageStore } from "../service-detail-page.lib";

/** Модалка «Регистрация версии …»: метка, область действия (дороги из справочника, как в заявке), состав стадий. Открывается через стор. */
export const RegisterVersionModal = observer(() => {
  const store = usePageStore()!;
  const isOpen = store.popupAction?.type === "register-version";
  const { service } = useServiceDetail();
  const { railways } = useRailways();
  const [label, setLabel] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRoads, setSelectedRoads] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);

  const roadOptions = useMemo<CreateApplicationSelectOption[]>(() => {
    if (railways.length) {
      return railways;
    }
    // Бэкенд справочника недоступен — показываем мок-дороги.
    return MOCK_CONSTRUCTOR_ROADS.map((road) => ({ value: road.id, label: road.name }));
  }, [railways]);

  const filteredRoads = useMemo(
    () => roadOptions.filter((road) => road.label.toLowerCase().includes(search.trim().toLowerCase())),
    [roadOptions, search],
  );

  const handleClose = useCallback(() => {
    setLabel("");
    setSearch("");
    setSelectedRoads([]);
    setSelectedStages([]);
    store.setPopupAction(null);
  }, [store]);

  return (
    <Modal.Root isOpen={isOpen} setIsOpen={(open) => (open ? null : handleClose())}>
      <Modal.Content className="w-[43.5rem] max-w-[90vw]">
        <Modal.Title>Регистрация версии</Modal.Title>
        <AdminModalBody>
          <h4 className="heading-h4 text-type-common">
            Регистрация версии <span className="text-type-minor">{service?.title}</span>
          </h4>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <p className="paragraph-sm text-type-common">Метка версии *</p>
              <input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Например, v3.0, 01.01.27"
                className="bg-neutrals-95 paragraph-base placeholder:text-type-light h-10 rounded-xl px-4 outline-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="paragraph-sm text-type-common">Область действия</p>
              <div className="bg-neutrals-95 flex h-9 items-center gap-2 rounded-xl px-3">
                <IconSearch className="text-neutrals-60 size-4 shrink-0" aria-hidden />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Поиск"
                  className="paragraph-sm placeholder:text-type-light w-full bg-transparent outline-none"
                />
                {search.length > 0 ? (
                  <button
                    type="button"
                    aria-label="Очистить поиск"
                    className="text-neutrals-60 hover:text-type-common flex size-5 shrink-0 items-center justify-center transition-colors"
                    onClick={() => setSearch("")}
                  >
                    <IconClose className="size-4" aria-hidden />
                  </button>
                ) : null}
              </div>
              <div className="border-neutrals-90 flex max-h-48 flex-col overflow-y-auto border-b">
                {filteredRoads.map((road) => (
                  <div key={road.value} className="border-neutrals-90 flex items-center justify-between border-y py-2">
                    <Checkbox
                      checked={selectedRoads.includes(road.value)}
                      onChange={(checked) =>
                        setSelectedRoads((current) => (checked ? [...current, road.value] : current.filter((value) => value !== road.value)))
                      }
                      label={road.label}
                    />
                    <span className="paragraph-sm text-type-minor">{road.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <p className="paragraph-sm text-type-common">Состав стадий *</p>
              <div className="border-neutrals-90 flex flex-col border-b">
                {MOCK_SERVICE_PROCESS_VERSIONS[0]?.status ? (
                  <p className="paragraph-sm text-type-light mb-2">
                    Входной контроль; Операторская служба; 1 / В работе у аппарата НГ; Аппарат НГ; 4 / Формируется решение; Операторская служба; 1 /
                    Выдано ТУ; Операторская служба; —
                  </p>
                ) : null}
                {MOCK_SERVICE_PROCESS_STEPS.map((step) => (
                  <div key={step.id} className="border-neutrals-90 flex items-center justify-between border-y py-2">
                    <Checkbox
                      checked={selectedStages.includes(step.id)}
                      onChange={(checked) =>
                        setSelectedStages((current) => (checked ? [...current, step.id] : current.filter((id) => id !== step.id)))
                      }
                      label={`${step.order} · ${step.title}`}
                    />
                    <span className="paragraph-sm text-type-minor">{step.performer}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </AdminModalBody>

        <AdminModalFooter>
          {/* TODO(FRONT-1390): регистрация версии — после появления бэкенда. */}
          <Button theme="primary" size="lg" onClick={handleClose}>
            Зарегистрировать
          </Button>
          <Button theme="secondary" size="lg" onClick={handleClose}>
            Отмена
          </Button>
        </AdminModalFooter>
      </Modal.Content>
    </Modal.Root>
  );
});
